import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
  resolveCoiLink,
  authorizeCoiUpload,
  analyzeCoiUpload,
  confirmCoiRenewal,
  type CoiAnalysis,
} from '../api';

type Stage = 'loading' | 'dead_link' | 'identity' | 'attach' | 'confirm' | 'done';

const COVERAGES = [
  { field: 'glExpiresAt', ocr: 'gl', label: 'General Liability' },
  { field: 'autoExpiresAt', ocr: 'auto', label: 'Automobile Liability' },
  { field: 'wcExpiresAt', ocr: 'wc', label: 'Workers Compensation' },
  { field: 'umbrellaExpiresAt', ocr: 'umbrella', label: 'Umbrella / Excess' },
] as const;

const LIMITS = [
  { key: 'glEachOccurrence', label: 'GL each occurrence' },
  { key: 'glAggregate', label: 'GL general aggregate' },
  { key: 'autoCombinedSingle', label: 'Auto combined single limit' },
  { key: 'wcEmployersLiability', label: "Employer's liability, each accident" },
  { key: 'umbrellaEachOccurrence', label: 'Umbrella each occurrence' },
] as const;

/**
 * A limit is pre-filled only at FULL confidence — one money value on the label's line.
 * Anything the parser guessed at (several amounts, and it took the largest) arrives
 * blank and holds the submit button until the sub fills it.
 *
 * Deliberately strict. Making someone type a number is cheap; a guessed aggregate
 * limit on an auditor's spreadsheet is not.
 */
const CONFIDENT = 1;

/**
 * Magic-link certificate renewal.
 *
 * The link is open by design — subs forward these to their insurance agent, and an
 * agent-completable link is the single biggest thing that gets certificates filed.
 * The control is the identity step, which also stops a stranger holding a forwarded
 * link from seeing the company's coverage dates.
 *
 * Upload is two phases: attach the certificate, then confirm what we read off it. If
 * OCR could not read the document at all, every cell simply arrives empty and the sub
 * types the lot — the same as before any of this existed. Nobody is ever stuck
 * because a document-reading service was having a bad afternoon.
 */
export function CoiRenewalPage() {
  const { token = '' } = useParams();
  const [stage, setStage] = useState<Stage>('loading');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [agentEmail, setAgentEmail] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<CoiAnalysis | null>(null);
  const [dates, setDates] = useState<Record<string, string>>({});
  const [limits, setLimits] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    resolveCoiLink(token)
      .then((r) => {
        setCompanyName(r.companyName);
        setStage('identity');
      })
      .catch((e: Error) => {
        setError(e.message);
        setStage('dead_link');
      });
  }, [token]);

  /** True when the parser was sure enough that we can pre-fill and not block. */
  const confident = (key: string): boolean => (analysis?.limitConfidence[key] ?? 0) >= CONFIDENT;

  const hasDate = COVERAGES.some((c) => dates[c.field]);
  /**
   * Blocking is for cells where the parser READ something and was not sure of it —
   * those are the ones a human must look at, because a half-read number is worse than
   * no number.
   *
   * A field OCR did not read at all is NOT blocking. Demanding it would punish the sub
   * for a bad scan, and plenty of certificates legitimately carry no umbrella layer at
   * all. Blank there means "not captured", which is exactly what the audit export says.
   */
  const limitsOutstanding = LIMITS.filter(
    (l) => analysis?.limits[l.key] !== undefined && !confident(l.key) && !limits[l.key],
  );

  async function onIdentity(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await authorizeCoiUpload(token, email);
      setStage('attach');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onAttach(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    setError('');
    setBusy(true);
    try {
      const form = new FormData();
      form.append('email', email);
      form.append('coiFile', file);
      const result = await analyzeCoiUpload(token, form);
      setAnalysis(result);

      // Pre-fill only what we actually read. A date we did not read stays empty, and
      // a limit we were unsure of stays empty and blocking.
      const nextDates: Record<string, string> = {};
      for (const c of COVERAGES) {
        const value = result.dates[c.ocr];
        if (value) nextDates[c.field] = value.slice(0, 10);
      }
      setDates(nextDates);

      const nextLimits: Record<string, string> = {};
      for (const l of LIMITS) {
        if (confidentIn(result, l.key)) nextLimits[l.key] = result.limits[l.key] ?? '';
      }
      setLimits(nextLimits);
      setStage('confirm');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!analysis || !hasDate || limitsOutstanding.length > 0) return;
    setError('');
    setBusy(true);
    try {
      await confirmCoiRenewal(token, {
        email,
        draftId: analysis.draftId,
        agentEmail: agentEmail || undefined,
        limits,
        ...Object.fromEntries(COVERAGES.filter((c) => dates[c.field]).map((c) => [c.field, dates[c.field]])),
      });
      setStage('done');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (stage === 'loading') return <main className="coi-page"><p>Loading…</p></main>;

  if (stage === 'dead_link') {
    return (
      <main className="coi-page">
        <h1>This link has expired</h1>
        <p>{error}</p>
        <p>Email <a href="mailto:project@hts.pro">project@hts.pro</a> and we will send a new one.</p>
      </main>
    );
  }

  if (stage === 'done') {
    return (
      <main className="coi-page">
        <h1>Certificate received</h1>
        <p>
          Thanks — we have your certificate for <strong>{companyName}</strong> and it is being
          reviewed. Most reviews finish within one business day, and we will email you when it is
          approved. There is nothing else you need to do.
        </p>
      </main>
    );
  }

  return (
    <main className="coi-page">
      <h1>Insurance certificate — {companyName}</h1>

      {stage === 'identity' && (
        <form onSubmit={onIdentity}>
          <p>Confirm who you are so we can record who filed this certificate.</p>
          <label htmlFor="coi-email">Your email</label>
          <input
            id="coi-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
          <p className="hint">
            Use a company email address, the contact we have on file, or a registered agent address.
          </p>
          {error && <p role="alert" className="error">{error}</p>}
          <button type="submit" disabled={busy || !email}>Continue</button>
        </form>
      )}

      {stage === 'attach' && (
        <form onSubmit={onAttach}>
          <p>Attach the certificate. We will read the dates and limits off it so you only have to check them.</p>
          <label htmlFor="coi-file">Certificate (PDF or photo)</label>
          <input
            id="coi-file"
            type="file"
            accept="application/pdf,image/*"
            required
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {error && <p role="alert" className="error">{error}</p>}
          <button type="submit" disabled={busy || !file}>
            {busy ? 'Reading your certificate…' : 'Continue'}
          </button>
        </form>
      )}

      {stage === 'confirm' && analysis && (
        <form onSubmit={onConfirm}>
          {analysis.ocrStatus === 'failed' || analysis.ocrStatus === 'unreadable' ? (
            <p role="status">
              We could not read this certificate automatically, so please enter the details below.
              Your upload is safe — nothing is lost.
            </p>
          ) : (
            <p role="status">
              We read the details below off your certificate. Please check them, and fill in
              anything we left blank.
            </p>
          )}

          <h2>Expiry dates</h2>
          {COVERAGES.map((c) => (
            <div key={c.field}>
              <label htmlFor={c.field}>{c.label}</label>
              <input
                id={c.field}
                type="date"
                value={dates[c.field] ?? ''}
                onChange={(e) => setDates((d) => ({ ...d, [c.field]: e.target.value }))}
              />
            </div>
          ))}

          <h2>Coverage limits</h2>
          {LIMITS.map((l) => {
            const needsYou = !confident(l.key);
            return (
              <div key={l.key}>
                <label htmlFor={l.key}>
                  {l.label}
                  {needsYou && <span className="needs-you"> — please confirm</span>}
                </label>
                <input
                  id={l.key}
                  type="text"
                  inputMode="numeric"
                  aria-invalid={needsYou && !limits[l.key] ? true : undefined}
                  placeholder={needsYou ? 'We could not read this one' : ''}
                  value={limits[l.key] ?? ''}
                  onChange={(e) => setLimits((v) => ({ ...v, [l.key]: e.target.value }))}
                />
              </div>
            );
          })}

          <label htmlFor="agent-email">Your insurance agent&rsquo;s email (optional)</label>
          <input
            id="agent-email"
            type="email"
            value={agentEmail}
            onChange={(e) => setAgentEmail(e.target.value)}
          />
          <p className="hint">Add it and they will be able to file certificates for you directly.</p>

          {limitsOutstanding.length > 0 && (
            <p role="status" className="hint">
              Still needed: {limitsOutstanding.map((l) => l.label).join(', ')}.
            </p>
          )}
          {error && <p role="alert" className="error">{error}</p>}
          <button type="submit" disabled={busy || !hasDate || limitsOutstanding.length > 0}>
            Submit certificate
          </button>
        </form>
      )}
    </main>
  );
}

/** Confidence check against a freshly returned analysis, before it reaches state. */
function confidentIn(analysis: CoiAnalysis, key: string): boolean {
  return (analysis.limitConfidence[key] ?? 0) >= CONFIDENT;
}
